import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileText, Loader2, Download, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const fmtBRL = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

const STATUS_OS = {
  aguardando_execucao: { label: "AGUARD. EXECUÇÃO", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  em_execucao:         { label: "EM EXECUÇÃO",      cls: "bg-blue-100 text-blue-800 border-blue-300" },
  concluida:           { label: "CONCLUÍDA",        cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelada:           { label: "CANCELADA",        cls: "bg-red-100 text-red-700 border-red-300" },
};

export default function PosvendaOrdensServico() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canDelete = ["diretor", "gerente"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [delConf, setDelConf] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus !== "todos") params.status = filtroStatus;
      const { data } = await api.get("/posvenda/ordens-servico", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erro ao listar OS");
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  const baixarPDF = async (os) => {
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("lab_token");
      const resp = await fetch(`${BACKEND}/api/posvenda/ordens-servico/${os.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `OS_${os.numero}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error("Erro ao baixar");
    }
  };

  return (
    <div className="space-y-4" data-testid="posvenda-os-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-orange-600" />
            Ordens de Serviço
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            OS geradas a partir das Propostas Pós-venda aprovadas
          </p>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tabs value={filtroStatus} onValueChange={setFiltroStatus}>
            <TabsList>
              <TabsTrigger value="todos" data-testid="tab-todos">Todas</TabsTrigger>
              <TabsTrigger value="aguardando_execucao" data-testid="tab-aguardando">Aguardando</TabsTrigger>
              <TabsTrigger value="em_execucao" data-testid="tab-em-execucao">Em Execução</TabsTrigger>
              <TabsTrigger value="concluida" data-testid="tab-concluida">Concluídas</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            Nenhuma OS encontrada. As Ordens de Serviço são geradas a partir de Propostas aprovadas.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Nº OS</TableHead>
                <TableHead className="w-32">Proposta</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="w-32">Técnico</TableHead>
                <TableHead className="w-32 text-right">Valor</TableHead>
                <TableHead className="w-36">Status</TableHead>
                <TableHead className="w-28">Data</TableHead>
                <TableHead className="w-32 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((o) => {
                const s = STATUS_OS[o.status] || { label: o.status, cls: "bg-slate-100" };
                return (
                  <TableRow key={o.id} data-testid={`row-os-${o.id}`}>
                    <TableCell className="font-mono text-xs">{o.numero}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{o.proposta_numero}</TableCell>
                    <TableCell>
                      <div className="text-sm">{o.cliente_nome}</div>
                      <div className="text-[10px] text-slate-500">{o.cliente_cnpj || "—"}</div>
                    </TableCell>
                    <TableCell className="text-xs">{o.tecnico_nome || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtBRL(o.total)}</TableCell>
                    <TableCell>
                      <Badge className={`${s.cls} border text-[10px] font-mono`}>{s.label}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{fmtData(o.criado_em)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => baixarPDF(o)}
                          data-testid={`btn-pdf-${o.id}`} title="Baixar PDF">
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => navigate(`/app/posvenda/propostas?os=${o.proposta_id}`)}
                          data-testid={`btn-ver-prop-${o.id}`} title="Ver proposta">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                        {canDelete && (
                          <Button size="sm" variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => setDelConf(o)}
                            data-testid={`btn-del-os-${o.id}`} title="Excluir OS">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!delConf} onOpenChange={(o) => !o && setDelConf(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir OS?</DialogTitle>
            <DialogDescription>
              {delConf && <>OS <strong>{delConf.numero}</strong> será excluída (soft delete). O PDF e dados auditáveis serão preservados.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConf(null)}>Cancelar</Button>
            <Button onClick={async () => {
              try {
                await api.delete(`/posvenda/ordens-servico/${delConf.id}`);
                toast.success("OS excluída");
                setDelConf(null);
                carregar();
              } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
            }} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-del-os">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
